import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { supabase } from '../../services/supabase'
import { useToast } from '../../components/ui/Toast'

interface ResetPasswordForm {
  password: string
  confirmPassword: string
}

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const { success: showSuccessToast, error: showErrorToast } = useToast()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<ResetPasswordForm>()

  const watchPassword = watch('password')

  // Always show the password reset form when accessing this page via the reset link.
  // The presence of recovery tokens in the URL indicates this is a password reset flow.
  useEffect(() => {
    const checkIfValidResetLink = () => {
      // Check if URL contains recovery parameters (access_token, refresh_token, type=recovery)
      const urlParams = new URLSearchParams(window.location.hash.substring(1))
      const urlSearchParams = new URLSearchParams(window.location.search)
      
      const hasAccessToken = urlParams.get('access_token') || urlSearchParams.get('access_token')
      const hasRecoveryType = urlParams.get('type') === 'recovery' || urlSearchParams.get('type') === 'recovery'
      
      // If this is not a password reset link (no recovery tokens), redirect to login
      if (!hasAccessToken && !hasRecoveryType) {
        showErrorToast('Invalid password reset link. Please request a new password reset.')
        setTimeout(() => navigate('/login'), 2000)
      }
      
      // Otherwise, always show the password reset form regardless of authentication state
      // This ensures users can set a new password even if they're already signed in
    }

    checkIfValidResetLink()
  }, [location, navigate, showErrorToast])

  const onSubmit = async (data: ResetPasswordForm) => {
    try {
      setIsLoading(true)
      
      const { error } = await supabase.auth.updateUser({
        password: data.password
      })
      
      if (error) {
        showErrorToast(`Failed to update password: ${error.message}`)
        return
      }

      setIsSuccess(true)
      showSuccessToast({
        title: 'Password Updated Successfully!',
        message: 'Your password has been changed. You will be redirected to the homepage.'
      })
      
      // Redirect to homepage after success
      setTimeout(() => {
        navigate('/')
      }, 3000)
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update password'
      showErrorToast(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              <span
                className="cursor-pointer hover:text-blue-600 transition-colors"
                onClick={() => navigate('/')}
                title="Go to Homepage"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/') }}
                role="link"
              >
                TyreLink
              </span>
            </h1>
            <p className="text-lg text-gray-600 mb-8">Tyre Marketplace</p>
          </div>
        </div>

        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-lg sm:rounded-xl sm:px-10">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Password Updated!</h2>
              <p className="text-gray-600 mb-6">
                Your password has been successfully updated. You will be redirected to the homepage shortly.
              </p>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Continue to TyreLink
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            <span
              className="cursor-pointer hover:text-blue-600 transition-colors"
              onClick={() => navigate('/')}
              title="Go to Homepage"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/') }}
              role="link"
            >
              TyreLink
            </span>
          </h1>
          <p className="text-lg text-gray-600 mb-8">Tyre Marketplace</p>
        </div>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-xl sm:px-10">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 text-center">Set New Password</h2>
            <p className="text-gray-600 text-center mt-2">
              Enter your new password below to complete the reset process.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                    message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
                  }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your new password"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Password must be at least 8 characters with uppercase, lowercase, and number
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: value => value === watchPassword || 'Passwords do not match'
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm your new password"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating Password...
                  </div>
                ) : (
                  'Update Password'
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  Back to Login
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage